import { useMemo, useRef, useState } from 'react';
import { FileDropzone } from '../ui/FileDropzone';
import { MonthSelector } from '../ui/MonthSelector';
import { OptionsPanel } from '../ui/OptionsPanel';
import { PrivacyNotice } from '../ui/PrivacyNotice';
import { AnalysisSummary, GenerationSummary } from '../ui/ProcessingSummary';
import { DownloadPanel } from '../ui/DownloadPanel';
import type { AnalysisResult, GeneratedWorkbook, GenerationResult, ProcessingSummary, UploadedAfdFile } from '../core/types';

async function toUploadedFiles(files: File[]): Promise<UploadedAfdFile[]> {
  return Promise.all(files.map(async (file) => ({ name: file.name, data: await file.arrayBuffer() })));
}

type WorkerResponse =
  | { type: 'analysis-result'; result: AnalysisResult }
  | { type: 'generation-result'; result: GenerationResult }
  | { type: 'error'; message: string };

function createWorker(): Worker {
  return new Worker(new URL('../worker/processFiles.worker.ts', import.meta.url), { type: 'module' });
}

export default function App() {
  const [files, setFiles] = useState<File[]>([]);
  const [analysis, setAnalysis] = useState<AnalysisResult | null>(null);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [keepDuplicates, setKeepDuplicates] = useState(false);
  const [nearDuplicateMinutes, setNearDuplicateMinutes] = useState(2);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [summary, setSummary] = useState<ProcessingSummary | null>(null);
  const [compiledWorkbook, setCompiledWorkbook] = useState<GeneratedWorkbook | null>(null);
  const [rawWorkbook, setRawWorkbook] = useState<GeneratedWorkbook | null>(null);
  const workerRef = useRef<Worker | null>(null);

  const canAnalyze = files.length > 0 && !busy;
  const canGenerate = files.length > 0 && selectedMonth && !busy;

  const fileKey = useMemo(() => files.map((file) => `${file.name}:${file.size}:${file.lastModified}`).join('|'), [files]);

  function resetResults() {
    setAnalysis(null);
    setSelectedMonth('');
    setSummary(null);
    setCompiledWorkbook(null);
    setRawWorkbook(null);
    setError('');
  }

  function handleFiles(nextFiles: File[]) {
    setFiles(nextFiles);
    resetResults();
  }

  async function runWorker<T extends WorkerResponse>(post: (worker: Worker, files: UploadedAfdFile[]) => void): Promise<T> {
    workerRef.current?.terminate();
    const worker = createWorker();
    workerRef.current = worker;
    const uploaded = await toUploadedFiles(files);
    return new Promise((resolve, reject) => {
      worker.onmessage = (event: MessageEvent<WorkerResponse>) => {
        worker.terminate();
        workerRef.current = null;
        if (event.data.type === 'error') reject(new Error(event.data.message));
        else resolve(event.data as T);
      };
      worker.onerror = (event) => {
        worker.terminate();
        workerRef.current = null;
        reject(new Error(event.message || 'Erro no processamento.'));
      };
      post(worker, uploaded);
    });
  }

  async function analyze() {
    if (!files.length) return;
    setBusy(true);
    setStatus('Analisando arquivos localmente...');
    setError('');
    try {
      const response = await runWorker<{ type: 'analysis-result'; result: AnalysisResult }>((worker, uploaded) => {
        worker.postMessage({ type: 'analyze', files: uploaded, keepDuplicates, nearDuplicateMinutes }, uploaded.map((file) => file.data));
      });
      setAnalysis(response.result);
      setSelectedMonth(response.result.latestPeriodLabel ?? '');
      setStatus('Análise concluída.');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setStatus('');
    } finally {
      setBusy(false);
    }
  }

  async function generate() {
    if (!files.length || !selectedMonth) return;
    setBusy(true);
    setStatus('Gerando planilhas no navegador...');
    setError('');
    setSummary(null);
    setCompiledWorkbook(null);
    setRawWorkbook(null);
    try {
      const response = await runWorker<{ type: 'generation-result'; result: GenerationResult }>((worker, uploaded) => {
        worker.postMessage({ type: 'generate', files: uploaded, options: { month: selectedMonth, keepDuplicates, nearDuplicateMinutes } }, uploaded.map((file) => file.data));
      });
      setSummary(response.result.summary);
      setCompiledWorkbook(response.result.compiledWorkbook);
      setRawWorkbook(response.result.rawWorkbook);
      if (!analysis) {
        setAnalysis({ availablePeriods: response.result.availablePeriods, latestPeriodLabel: response.result.availablePeriods.at(-1)?.label ?? null, preflightSummary: { ...response.result.summary, shiftsAvailable: response.result.summary.shiftsFormed } });
      }
      setStatus('Planilhas geradas.');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setStatus('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="page" key={fileKey}>
      <header className="hero">
        <div>
          <p className="eyebrow">Controle de ponto AFD</p>
          <h1>Gere planilhas de ponto sem enviar arquivos para a nuvem.</h1>
          <p className="hero-copy">Ferramenta estática para cálculo local de jornadas, duplicatas e auditoria.</p>
        </div>
      </header>

      <PrivacyNotice />

      <div className="layout">
        <div className="main-column">
          <FileDropzone files={files} onFiles={handleFiles} />
          <AnalysisSummary analysis={analysis} />
          <GenerationSummary summary={summary} />
          <DownloadPanel compiled={compiledWorkbook} raw={rawWorkbook} />
        </div>

        <aside className="side-column">
          <OptionsPanel
            keepDuplicates={keepDuplicates}
            setKeepDuplicates={setKeepDuplicates}
            nearDuplicateMinutes={nearDuplicateMinutes}
            setNearDuplicateMinutes={setNearDuplicateMinutes}
          />
          <MonthSelector periods={analysis?.availablePeriods ?? []} selected={selectedMonth} setSelected={setSelectedMonth} />
          <section className="card action-card">
            <button disabled={!canAnalyze} onClick={analyze}>Analisar arquivos</button>
            <button className="primary" disabled={!canGenerate} onClick={generate}>Gerar planilhas</button>
            {busy && <p className="muted">{status}</p>}
            {!busy && status && <p className="success-text">{status}</p>}
            {error && <p className="error-text">{error}</p>}
          </section>
        </aside>
      </div>
    </main>
  );
}
